import { useState } from "react";
import { Mail } from "lucide-react";
import { Card, Section } from "../Layout";
import { cx } from "../helpers";
import { useI18n } from "../i18n";

const FORMSPREE_URL =
  process.env.REACT_APP_FORMSPREE_URL || "https://formspree.io/f/mvzwzjgp";
function Field({ label, className, ...props }) {
  return (
    <label className={cx("grid gap-2", className)}>
      <span className="text-xs tracking-[0.25em] uppercase text-theme-text-faint">{label}</span>
      <input
        {...props}
        className="w-full rounded-2xl border border-theme-border bg-theme-surface px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-theme-accent-light"
      />
    </label>
  );
}

function TextArea({ label, className, ...props }) {
  return (
    <label className={cx("grid gap-2", className)}>
      <span className="text-xs tracking-[0.25em] uppercase text-theme-text-faint">{label}</span>
      <textarea
        {...props}
        rows={4}
        className="w-full rounded-2xl border border-theme-border bg-theme-surface px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-theme-accent-light resize-none"
      />
    </label>
  );
}

function Select({ label, children, className, ...props }) {
  return (
    <label className={cx("grid gap-2", className)}>
      <span className="text-xs tracking-[0.25em] uppercase text-theme-text-faint">{label}</span>
      <select
        {...props}
        className="w-full rounded-2xl border border-theme-border bg-theme-surface px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-theme-accent-light"
      >
        {children}
      </select>
    </label>
  );
}

export default function RSVP() {
  const { t } = useI18n();
  const [status, setStatus] = useState({ type: "idle", msg: "" });

  const submit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    setStatus({ type: "loading", msg: t("rsvp.sending") || "Sending..." });

    const formData = new FormData(form);
    
    try {
      const response = await fetch(FORMSPREE_URL, {
        method: "POST",
        body: formData,
        headers: {
          Accept: "application/json",
        },
      });

      const data = await response.json();

      if (response.ok || data.ok) {
        setStatus({ type: "ok", msg: t("rsvp.success") });
        form.reset();
      } else {
        console.error("Formspree error:", data);
        setStatus({ type: "error", msg: data.error || t("rsvp.error") || "Something went wrong. Please try again." });
      }
    } catch (error) {
      console.error("Submit error:", error);
      setStatus({ type: "error", msg: t("rsvp.error") || "Something went wrong. Please try again." });
    }
  };

  return (
    <Section id="rsvp" eyebrow={t("rsvp.eyebrow")} title={t("rsvp.title")} icon={Mail}>
      <div className="grid lg:grid-cols-12 gap-4 items-start">
        <Card className="lg:col-span-7 p-6 sm:p-8">
          <form onSubmit={submit} className="grid gap-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t("rsvp.full_name")} name="name" placeholder={t("rsvp.full_name_placeholder")} required />
              <Field
                label={t("rsvp.email")}
                name="email"
                placeholder={t("rsvp.email_placeholder")}
                type="email"
                required
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <Select label={t("rsvp.attending")} name="attending" required>
                <option value="">{t("rsvp.select")}</option>
                <option value="yes">{t("rsvp.attending_yes")}</option>
                <option value="no">{t("rsvp.attending_no")}</option>
              </Select>

              <Select label={t("rsvp.plus_one")} name="plusOne">
                <option value="">{t("rsvp.select")}</option>
                <option value="no">{t("rsvp.no")}</option>
                <option value="yes">{t("rsvp.yes")}</option>
              </Select>
            </div>

            <Field label={t("rsvp.dietary")} name="diet" placeholder={t("rsvp.dietary_placeholder")} />
            <TextArea label={t("rsvp.message")} name="message" placeholder={t("rsvp.message_placeholder")} />

            <button 
              type="submit"
              disabled={status.type === "loading"}
              className="rounded-2xl bg-theme-primary text-theme-primary-text px-5 py-3 text-sm shadow-sm hover:shadow-md transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {status.type === "loading" ? (t("rsvp.sending") || "Sending...") : t("rsvp.submit")}
            </button>

            {status.type !== "idle" && status.type !== "loading" && (
              <p className={cx("text-sm", status.type === "ok" ? "text-emerald-700" : "text-rose-700")}>
                {status.msg}
              </p>
            )}
          </form>
        </Card>

        <Card className="lg:col-span-5 p-6 sm:p-8">
          <h3 className="font-serif text-2xl">{t("rsvp.contact_title")}</h3>
          <p className="mt-3 text-sm text-theme-text-muted">{t("rsvp.contact_text")}</p>

          <div className="mt-6 grid gap-3">
            <div className="rounded-2xl border border-theme-border bg-theme-surface px-4 py-4">
              <p className="text-xs tracking-[0.22em] uppercase text-theme-text-faint">{t("rsvp.email_label")}</p>
              <p className="mt-2 text-sm text-theme-text-muted">{t("rsvp.contact_email")}</p>
            </div>
          </div>
        </Card>
      </div>
    </Section>
  );
}
